import { useHistory } from 'react-router-dom';
import useMountEffect from '@dashboard/hooks/useMountEffect';

const POLL_MS = 100;
const GIVE_UP_MS = 2000;
const HIGHLIGHT_MS = 1500;
const HIGHLIGHT_CLASSES = ['ring-2', 'ring-ring', 'ring-offset-2', 'ring-offset-background'];
const FOCUSABLE =
  'input:not([type="hidden"]), select, textarea, button, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

// Form hides fields it doesn't show with the `hidden` class rather than unmounting them.
const isShown = element => !element.closest('[hidden], .hidden');

function findByKey(key) {
  const tagged = [...document.querySelectorAll('[data-ai-focus]')].filter(
    element => element.dataset.aiFocus === key,
  );
  return [...tagged, document.getElementById(key), ...document.getElementsByName(key)].find(
    element => element && isShown(element),
  );
}

const focusableIn = element =>
  element.matches(FOCUSABLE) ? element : [...element.querySelectorAll(FOCUSABLE)].find(isShown);

const namedIn = (region, name) =>
  name &&
  [...region.querySelectorAll('[name]')].find(
    element => element.getAttribute('name') === name && isShown(element),
  );

function highlight(element) {
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView?.({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  element.focus({ preventScroll: true });
  const added = HIGHLIGHT_CLASSES.filter(name => !element.classList.contains(name));
  element.classList.add(...added);
  setTimeout(() => element.classList.remove(...added), HIGHLIGHT_MS);
}

// Waits for the input a change touched to render, opening its record's row when it sits in a
// collapsed list, then focuses and rings it. Returns a cancel function.
export function focusAIChange({ key, input }, onDone) {
  const deadline = Date.now() + GIVE_UP_MS;
  let timer;
  let opened = false;
  let fallback;

  const locate = () => {
    const found = findByKey(key);
    if (!found) return undefined;
    // Only a record's row opens: a select trigger also controls a region, its option list.
    const regionId = found.dataset.aiFocus === key && found.getAttribute('aria-controls');
    if (!regionId) return focusableIn(found) ?? found;
    fallback = found;
    if (found.getAttribute('aria-expanded') !== 'true') {
      if (!opened) found.click();
      opened = true;
      return undefined;
    }
    const region = document.getElementById(regionId);
    return region && (namedIn(region, input) || focusableIn(region));
  };

  const tick = () => {
    const element = locate() ?? (Date.now() >= deadline ? (fallback ?? null) : undefined);
    if (element === undefined) {
      timer = setTimeout(tick, POLL_MS);
      return;
    }
    if (element) highlight(element);
    onDone();
  };

  tick();
  return () => clearTimeout(timer);
}

// Mounted once per navigation (keyed by location) when Open in the AI editor's changes passes a
// focus target; clears it after so a reload or Back doesn't focus again.
export default function useFocusAIChange(target) {
  const history = useHistory();
  useMountEffect(() =>
    focusAIChange(target, () => {
      const state = { ...history.location.state };
      delete state.aiFocus;
      history.replace({
        ...history.location,
        state: Object.keys(state).length ? state : undefined,
      });
    }),
  );
}

export function AIChangeFocus({ target }) {
  useFocusAIChange(target);
  return null;
}
